import { RouterProvider, createBrowserRouter, Outlet } from "react-router";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Start from "./pages/Start/Start.tsx";
import Activity from "./pages/Activity/Activity.tsx";
import React from "react";
import Api from "./services/Api.ts";
import AcceptInvite from "./pages/AcceptInvite/AcceptInvite.tsx";
import Categories from "./pages/Categories/Categories.tsx";
import Category from "./pages/Categories/Category.tsx";
import Survey from "./pages/Survey/Survey.tsx";
import SurveyRequests from "./pages/Survey/SurveyRequests.tsx";
import SurveyRequest from "./pages/Survey/SurveyRequest.tsx";
import Kpi from "./pages/Kpi/Kpi.tsx";
import { useSetAtom } from 'jotai'
import { categoriesAtom, departmentsAtom } from './jotai_state'
import NotFound from "./pages/NotFound.tsx";
import SprintMenu from "./pages/Sprint/SprintMenu.tsx";
import TopHeader from "./components/TopHeader/TopHeader.tsx";

interface Props {
  outerRoute: string|undefined
}

const AppRouter = (props: Props) => {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();
  const [enoughPrivileges, setEnoughPrivileges] = useState(false)
  const [loaded, setLoaded] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [admin, setAdmin] = useState(false);
  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const setCategories = useSetAtom(categoriesAtom);
  const setDepartments = useSetAtom(departmentsAtom);
  const api = Api.getInstance(getAccessTokenSilently);
  const invitationPage = window.location.pathname.startsWith('/accept_invite/');


  let routes = [
    {
      path: '/',
      element: (
        <Start />
      )
    },
    {
      path: '/activity',
      element: (
        <Activity />
      )
    },
    {
      path: '/signals',
      element: (
        <SprintMenu />
      )
    },
    {
      path: '/kpi',
      element: (
        <Kpi />
      )
    },
    {
      path: '/categories',
      element: (
        <Categories />
      )
    },
    {
      path: '/categories/:categoryId',
      element: (
        <Category />
      )
    },
    {
      path: '/categories/survey/:surveyId',
      element: (
        <Survey />
      )
    },
    {
      path: '/categories/survey/:surveyId/requests',
      element: (
        <SurveyRequests />
      )
    },
    {
      path: '/categories/request/:requestId',
      element: (
        <SurveyRequest />
      )
    },
    {
      path: '/accept_invite/:code',
      element: (
        <AcceptInvite setCustomerName={setCustomerName}/>
      )
    },
    {
      path: '*',
      element: (
        <NotFound />
      )
    },
  ]

  useEffect(() => {
    if (props.outerRoute) {
      router.navigate(props.outerRoute);
    }
  }, [props.outerRoute]);

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        const loginOptions = {
          appState: {
            returnTo: window.location.pathname,
          },
          authorizationParams: {}
        };

        if (invitationPage) {
          loginOptions.authorizationParams = {screen_hint: "signup"};
        }
        loginWithRedirect(loginOptions);
      } else {
        // There is an unknown problem of getAccessTokenSilently failing to get refresh token. Try this method
        // here, and if it fails, run the loginWithRedirect method to get a new refresh token.
        getAccessTokenSilently().then((accessToken: string) => {
          console.log("Got access token, length " + accessToken.length);
          setEnoughPrivileges(true);
        }).catch((reason: any) => {
          console.log(reason);
          const loginOptions = {
            appState: {
              returnTo: window.location.pathname,
            }
          };
          loginWithRedirect(loginOptions).then();
        })
      }
    }
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (enoughPrivileges) {
      if (invitationPage) {
        setCustomerName('---')
        setLoaded(true)
      } else {
        api.get('customer/bootstrap', ['customer_not_found']).then(response => {
          setCustomerName(response.json.customerName);
          setFirstName(response.json.firstName);
          setCategories(response.json.categories);
          setDepartments(response.json.departments);
          setAdmin(response.json.admin);
          setLoaded(true)
        }).catch(error => {
          if (error.isExpectedError) {
            setError(error.json.message)
            setLoaded(true)
          } else {
            alert('Unknown error (http status ' + error.response.status + ')')
          }
        })
      }
    }
  }, [enoughPrivileges]);



  const App = () => {
    return (
      <>
        <TopHeader admin={admin} clientName={firstName} customerName={customerName}/>
        <Outlet/>
      </>
    );
  }

  const router = createBrowserRouter([
    {
      path: "/",
      element: <App />,
      children: routes,
    },
  ]);

  const renderMain = () => {
    if (!enoughPrivileges) {
      return (<div className="mt-4 text-center">Logging in...</div>)
    } else if (!loaded) {
      return (<div className="mt-4 text-center">Loading data...</div>)
    } else if (error.length > 0) {
      return (<div className="mt-4 text-center">{error}</div>)
    } else {
      return <RouterProvider router={router} />
    }
  }

  return (
    <>
      {renderMain()}
    </>
  )
}

export default AppRouter;